import { useMemo, useState, type ReactNode } from 'react';

import type { UiDocumentV1, UiNavigationContributionV1 } from '../../mdr-ui-contracts/src/index.js';

interface NavigationItem {
  readonly screen: UiDocumentV1;
  readonly navigation: UiNavigationContributionV1;
}

interface NavigationGroup {
  readonly groupId: string;
  readonly groupCopyKey: string;
  readonly groupOrder: number;
  readonly defaultOpen: boolean;
  readonly items: readonly NavigationItem[];
}

const ICON_GLYPHS: Readonly<Record<string, string>> = Object.freeze({
  dashboard: '▦',
  booking: '▤',
  pos: '▣',
  analytics: '▥',
  payments: '◫',
  customers: '◎',
  team: '♙',
  settings: '⚙',
  audit: '◴',
  notifications: '◉',
  menu: '☷',
  resource: '▦',
  blueprint: '◇',
  waitlist: '⌛',
  takeout: '↗',
  invoice: '▤',
  expense: '$',
  play: '▷',
  module: '□',
});

function navigationFor(screen: UiDocumentV1): UiNavigationContributionV1 {
  return (
    screen.navigation ?? {
      labelCopyKey: screen.titleCopyKey,
      groupId: 'ops.more',
      groupCopyKey: 'ops.nav.more',
      order: 1_000,
      groupOrder: 1_000,
      iconId: 'module',
      defaultOpen: false,
      standalone: false,
    }
  );
}

function compareItems(left: NavigationItem, right: NavigationItem): number {
  return (
    left.navigation.order - right.navigation.order ||
    left.screen.screenId.localeCompare(right.screen.screenId)
  );
}

function groupScreens(screens: readonly UiDocumentV1[]): {
  readonly standalone: readonly NavigationItem[];
  readonly groups: readonly NavigationGroup[];
} {
  const items = screens.map((screen) => ({ screen, navigation: navigationFor(screen) }));
  const standalone = items.filter(({ navigation }) => navigation.standalone).sort(compareItems);
  const grouped = new Map<string, NavigationItem[]>();
  for (const item of items.filter(({ navigation }) => !navigation.standalone)) {
    const existing = grouped.get(item.navigation.groupId) ?? [];
    existing.push(item);
    grouped.set(item.navigation.groupId, existing);
  }
  const groups = [...grouped.entries()]
    .map(([groupId, groupItems]): NavigationGroup => {
      const first = groupItems[0]!.navigation;
      return {
        groupId,
        groupCopyKey: first.groupCopyKey,
        groupOrder: first.groupOrder,
        defaultOpen: groupItems.some(({ navigation }) => navigation.defaultOpen),
        items: groupItems.sort(compareItems),
      };
    })
    .sort(
      (left, right) =>
        left.groupOrder - right.groupOrder || left.groupId.localeCompare(right.groupId),
    );
  return { standalone, groups };
}

function icon(iconId: string): string {
  return ICON_GLYPHS[iconId] ?? ICON_GLYPHS['module']!;
}

export function OpsProductFrame({
  screens,
  selectedScreenId,
  copy,
  onSelectScreen,
  children,
}: {
  readonly screens: readonly UiDocumentV1[];
  readonly selectedScreenId: string;
  readonly copy: Readonly<Record<string, string>>;
  readonly onSelectScreen: (screenId: string) => void;
  readonly children: ReactNode;
}) {
  const navigation = useMemo(() => groupScreens(screens), [screens]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Readonly<Record<string, boolean>>>(() =>
    Object.fromEntries(navigation.groups.map((group) => [group.groupId, group.defaultOpen])),
  );
  const brandTitle = copy['brand.app-title'] ?? copy['ops.shell.title'] ?? '店家後台';
  const roleLabel = copy['ops.shell.role'] ?? '工作人員';

  const select = (screenId: string) => {
    onSelectScreen(screenId);
    setMobileOpen(false);
  };

  const screenButton = (
    { screen, navigation: itemNavigation }: NavigationItem,
    nested: boolean,
  ) => {
    const active = screen.screenId === selectedScreenId;
    return (
      <button
        key={screen.screenId}
        type="button"
        className="mdr-ops-nav__item"
        data-nested={nested ? 'true' : 'false'}
        data-active={active ? 'true' : 'false'}
        aria-current={active ? 'page' : undefined}
        onClick={() => select(screen.screenId)}
      >
        <span className="mdr-ops-nav__icon" aria-hidden="true">
          {icon(itemNavigation.iconId)}
        </span>
        <span>
          {copy[itemNavigation.labelCopyKey] ?? copy[screen.titleCopyKey] ?? screen.screenId}
        </span>
      </button>
    );
  };

  return (
    <div className="mdr-ops-shell">
      <header className="mdr-ops-header">
        <div className="mdr-ops-header__brand">
          <button
            type="button"
            className="mdr-ops-header__menu"
            aria-label={mobileOpen ? '關閉側邊選單' : '開啟側邊選單'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
          >
            ☰
          </button>
          <span className="mdr-ops-header__star" aria-hidden="true">
            ☆
          </span>
          <span>
            <strong>{brandTitle}</strong>
            <small>{copy['ops.shell.subtitle'] ?? '店家後台'}</small>
          </span>
        </div>
        <span className="mdr-ops-header__role">{roleLabel}</span>
      </header>

      <div className="mdr-ops-shell__body">
        {mobileOpen ? (
          <button
            type="button"
            className="mdr-ops-shell__scrim"
            aria-label="關閉側邊選單"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}
        <aside className="mdr-ops-sidebar" data-mobile-open={mobileOpen ? 'true' : 'false'}>
          <nav aria-label="主要導覽" className="mdr-ops-nav">
            {navigation.standalone.map((item) => screenButton(item, false))}
            {navigation.groups.map((group) => {
              const open = openGroups[group.groupId] ?? false;
              const hasActive = group.items.some(
                ({ screen }) => screen.screenId === selectedScreenId,
              );
              return (
                <section className="mdr-ops-nav__group" key={group.groupId}>
                  <button
                    type="button"
                    className="mdr-ops-nav__group-button"
                    data-active={hasActive ? 'true' : 'false'}
                    aria-expanded={open}
                    onClick={() =>
                      setOpenGroups((current) => ({ ...current, [group.groupId]: !open }))
                    }
                  >
                    <span aria-hidden="true">{open ? '⌄' : '›'}</span>
                    <span>
                      {copy[group.groupCopyKey] ??
                        (group.groupId === 'ops.more' ? '其他功能' : group.groupId)}
                    </span>
                  </button>
                  {open ? <div>{group.items.map((item) => screenButton(item, true))}</div> : null}
                </section>
              );
            })}
          </nav>
        </aside>
        <div className="mdr-ops-content">{children}</div>
      </div>
    </div>
  );
}
